import { useMemo, useState } from 'react';
import { Clock, HeartPulse, Loader2 } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useLegalSettings, replacePlaceholders } from '@/features/legal/hooks/useLegalSettings';
import { ConsentDialog } from './ConsentDialog';
import { ConsentCheckbox } from './ConsentCheckbox';
import { ConsentVersionStaleError, recordConsent } from '../api';
import type { ConsentSource, ConsentState } from '../types';

interface HealthStageProps {
  state: ConsentState;
  source: ConsentSource;
  onCompleted: () => void;
  onDecline: () => void;
  /** Nur aus den Profileinstellungen: dort darf der Dialog geschlossen werden. */
  onDismiss?: () => void;
}

/**
 * Stufe 2: Einwilligung in die Verarbeitung von Gesundheitsdaten
 * (DSGVO Art. 9).
 *
 * Ebenfalls blockierend, aber mit zwei Unterschieden zu Stufe 1:
 *   - die Folge einer Ablehnung wird beim Namen genannt, nicht umschrieben
 *   - die Entscheidung laesst sich einmalig um sieben Tage aufschieben
 *
 * Der Text kommt aus legal_settings.health_consent_content, damit er
 * versionierbar ist und im Admin-Panel gepflegt werden kann. Der i18n-Text
 * dient nur als Rueckfall, solange die Spalte leer ist.
 */
export function HealthStage({
  state,
  source,
  onCompleted,
  onDecline,
  onDismiss,
}: HealthStageProps) {
  const { t } = useTranslation(['consent', 'common']);
  const { data: legalSettings } = useLegalSettings();

  const [accepted, setAccepted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const content = useMemo(() => {
    if (legalSettings?.health_consent_content) {
      return replacePlaceholders(legalSettings.health_consent_content, legalSettings);
    }
    return t('consent:health.fallbackContent');
  }, [legalSettings, t]);

  const submit = async (decision: 'granted' | 'deferred') => {
    setIsSaving(true);
    setError(null);
    try {
      await recordConsent({
        consentType: 'health_data',
        decision,
        version: state.versions.health_data,
        source,
      });
      onCompleted();
    } catch (err) {
      console.error('Recording health consent failed:', err);
      setError(
        err instanceof ConsentVersionStaleError
          ? t('consent:gate.versionChanged')
          : t('consent:gate.genericError')
      );
      setIsSaving(false);
    }
  };

  return (
    <ConsentDialog
      icon={<HeartPulse className="w-5 h-5 text-pp-orange" />}
      iconClassName="bg-pp-orange/20"
      title={t('consent:health.title')}
      subtitle={t('consent:health.subtitle')}
      footer={
        <>
          <button
            onClick={() => submit('granted')}
            disabled={!accepted || isSaving}
            className="w-full py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('consent:gate.saving')}
              </>
            ) : (
              t('consent:health.accept')
            )}
          </button>

          {state.can_defer_health && (
            <button
              onClick={() => submit('deferred')}
              disabled={isSaving}
              className="w-full py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Clock className="w-4 h-4" />
              {t('consent:health.defer')}
            </button>
          )}

          <button
            onClick={onDecline}
            disabled={isSaving}
            className="w-full py-2.5 rounded-xl bg-white/10 border border-white/20 text-white/70 font-medium hover:bg-white/20 disabled:opacity-50"
          >
            {t('consent:health.decline')}
          </button>

          {onDismiss && (
            <button
              onClick={onDismiss}
              disabled={isSaving}
              className="w-full py-2 text-white/50 text-sm hover:text-white/70 disabled:opacity-50"
            >
              {t('common:actions.close')}
            </button>
          )}
        </>
      }
    >
      <div
        className="legal-content prose prose-invert prose-sm max-w-none text-white/80 mb-4"
        dangerouslySetInnerHTML={{ __html: content }}
      />

      <div className="mb-4">
        <p className="text-white/70 text-sm font-medium mb-2">
          {t('consent:health.processedData')}
        </p>
        <ul className="text-white/60 text-sm space-y-1 ml-4 list-disc">
          <li>{t('consent:health.dataItem1')}</li>
          <li>{t('consent:health.dataItem2')}</li>
          <li>{t('consent:health.dataItem3')}</li>
          <li>{t('consent:health.dataItem4')}</li>
          <li>{t('consent:health.dataItem5')}</li>
        </ul>
      </div>

      <ConsentCheckbox
        checked={accepted}
        onChange={setAccepted}
        disabled={isSaving}
        label={t('consent:health.checkbox')}
        versionLabel={t('consent:health.version', { version: state.versions.health_data })}
      />

      <p className="text-white/40 text-xs mt-2">{t('consent:health.revocable')}</p>

      {/* Die Folge einer Ablehnung ist eine Kontoloeschung. Das gehoert vor
          die Entscheidung, nicht in einen Bestaetigungsdialog danach. */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4 mt-4">
        <p className="text-white/70 text-sm font-medium mb-1">
          {t('consent:health.consequenceTitle')}
        </p>
        <p className="text-white/60 text-sm">{t('consent:health.consequence')}</p>
      </div>

      {state.can_defer_health ? (
        <p className="text-white/40 text-xs mt-3">{t('consent:health.deferHint')}</p>
      ) : (
        <p className="text-white/40 text-xs mt-3">{t('consent:health.deferUsed')}</p>
      )}

      {error && (
        <div className="bg-red-500/20 text-red-300 text-sm p-3 rounded-[10px] mt-4">{error}</div>
      )}
    </ConsentDialog>
  );
}
